import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AccessibilityStatement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversalAccess, faTasks, faBullhorn, faQuestionCircle, faEnvelope } from '@fortawesome/free-solid-svg-icons';

function AccessibilityStatement() {
  const { countryCode } = useOutletContext() || { countryCode: 'canada' }; // Default to Canada if context is unavailable
  const themeClass = `theme-${countryCode || 'canada'}`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    
    // Signal to react-snap that this page has been rendered
    if (typeof window !== 'undefined' && window.document) {
      document.documentElement.setAttribute('data-prerendered', 'true');
      // Add a marker element that react-snap can detect
      const marker = document.createElement('div');
      marker.id = 'accessibility-content-loaded';
      marker.style.display = 'none';
      document.body.appendChild(marker);
    }
  }, []);

  return (
    <div className={`accessibility-statement-page ${themeClass}`} data-prerendered="true">
      <Helmet>
        <title>Accessibility Statement - OriginSelect</title>
        <meta name="description" content="OriginSelect Accessibility Statement - Our commitment to web accessibility and WCAG 2.1 compliance for all users." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <noscript>
        <div className="noscript-accessibility-content">
          <h2>OriginSelect Accessibility Statement</h2>
          <p><strong>WCAG Conformance:</strong> OriginSelect is partially conformant with WCAG 2.1 Level AA standards.</p>
          <p><strong>Accessibility Remediation Contact:</strong> If you encounter any accessibility barriers on our website, please contact our dedicated accessibility team at: <a href="mailto:admin@originselect.com">admin@originselect.com</a></p>
          <p>We aim to respond to all accessibility-related inquiries within 5 business days.</p>
          <hr />
          <p>For the full interactive accessibility statement with complete details on our conformance status, technical specifications, and remediation plans, please enable JavaScript or request a text-only version by emailing us.</p>
        </div>
      </noscript>
      <div className="accessibility-container">
        <header className="accessibility-header">
          <FontAwesomeIcon icon={faUniversalAccess} className="accessibility-main-icon" />
          <h1>Accessibility Statement</h1>
          <p className="last-updated">Last updated: May 12, 2025</p>
        </header>

        <section id="commitment">
          <div className="section-heading">
            <FontAwesomeIcon icon={faBullhorn} className="section-icon" />
            <h2>Our Commitment to Accessibility</h2>
          </div>
          <p>OriginSelect is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.</p>
          <p>We believe that everyone has the right to live with dignity, equality, comfort, and independence. We strive to make our website accessible to the widest possible audience, regardless of technology or ability.</p>
        </section>

        <section id="measures">
          <div className="section-heading">
            <FontAwesomeIcon icon={faTasks} className="section-icon" />
            <h2>Measures to Support Accessibility</h2>
          </div>
          <p>OriginSelect takes the following measures to ensure accessibility of our website:</p>
          <ul className="measures-list">
            <li>Include accessibility as part of our mission statement.</li>
            <li>Integrate accessibility into our procurement practices.</li>
            <li>Appoint an accessibility officer and/or ombudsperson.</li>
            <li>Provide continual accessibility training for our staff.</li>
            <li>Assign clear accessibility targets and responsibilities.</li>
            <li>Employ formal accessibility quality assurance methods.</li>
            <li>Regularly audit our website for accessibility issues using automated tools and manual testing.</li>
            <li>Strive to adhere to Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.</li>
          </ul>
        </section>

        <section id="conformance-status">
          <div className="section-heading">
            <FontAwesomeIcon icon={faQuestionCircle} className="section-icon" />
            <h2>Conformance Status</h2>
          </div>
          <p>The <a href="https://www.w3.org/WAI/standards-guidelines/wcag/" target="_blank" rel="noopener noreferrer">Web Content Accessibility Guidelines (WCAG)</a> defines requirements for designers and developers to improve accessibility for people with disabilities. It defines three levels of conformance: Level A, Level AA, and Level AAA.</p>
          <p>OriginSelect is partially conformant with WCAG 2.1 Level AA. Partially conformant means that some parts of the content do not fully conform to the accessibility standard. We are actively working to increase the accessibility and usability of our website and in doing so adhere to many of the available standards and guidelines.</p>
        </section>
        
        <section id="feedback">
          <div className="section-heading">
            <FontAwesomeIcon icon={faEnvelope} className="section-icon" />
            <h2>Feedback & Contact Information</h2>
          </div>
          <p>We welcome your feedback on the accessibility of OriginSelect. Please let us know if you encounter accessibility barriers on our website:</p>
          <ul className="contact-list">
            <li><strong>E-mail:</strong> <a href="mailto:admin@originselect.com">admin@originselect.com</a></li>
            {/* Add other contact methods if available, e.g., phone, address */}
            {/* <li><strong>Phone:</strong> [Your Phone Number]</li> */}
            {/* <li><strong>Postal Address:</strong> [Your Address]</li> */}
          </ul>
          <p>We try to respond to feedback within 5 business days.</p>
          <p>For further details on our commitment and to understand the technical specifications and limitations, please review the respective sections on this page.</p>
        </section>

        <section id="technical-specifications">
          <div className="section-heading">
            {/* Consider an icon for technical aspects, e.g., faCogs or faCode */}
            <h2>Technical Specifications</h2>
          </div>
          <p>Accessibility of OriginSelect relies on the following technologies to work with the particular combination of web browser and any assistive technologies or plugins installed on your computer:</p>
          <ul className="tech-list">
            <li>HTML</li>
            <li>WAI-ARIA</li>
            <li>CSS</li>
            <li>JavaScript</li>
          </ul>
          <p>These technologies are relied upon for conformance with the accessibility standards used.</p>
        </section>
        
        <section id="limitations">
          <div className="section-heading">
            {/* Consider an icon for limitations, e.g., faExclamationTriangle */}
            <h2>Limitations and Alternatives</h2>
          </div>
          <p>Despite our best efforts to ensure accessibility of OriginSelect, there may be some limitations. Please contact us if you observe an issue not listed below.</p>
          <p>Known limitations for OriginSelect:</p>
          <ul className="limitations-list">
            <li><strong>User-generated content:</strong> Comments from users may not always meet accessibility standards. We monitor user content and typically repair issues within 5 business days. Please use the contact information if you encounter an issue.</li>
            <li><strong>Third-party content:</strong> Some embedded third-party content (e.g., maps, videos) may not be fully accessible. We aim to select accessible third-party content where possible.</li>
            {/* Add more specific limitations if known */}
          </ul>
        </section>

        <section id="assessment-approach">
          <div className="section-heading">
            {/* Consider an icon for assessment, e.g., faClipboardCheck */}
            <h2>Assessment Approach</h2>
          </div>
          <p>OriginSelect assessed the accessibility of its website by the following approaches:</p>
          <ul className="assessment-list">
            <li>Self-evaluation</li>
            <li>Regularly using automated accessibility checking tools</li>
            {/* <li>External evaluation (if applicable)</li> */}
          </ul>
        </section>
        
        <section id="future-enhancements">
          <div className="section-heading">
            {/* Consider an icon for future plans, e.g., faLightbulb */}
            <h2>Future Enhancements</h2>
          </div>
          <p>We are committed to ongoing improvement and will continue to enhance the accessibility of our website. Future plans include:</p>
          <ul className="future-list">
            <li>Conducting a full external accessibility audit.</li>
            <li>Providing more detailed alternative text for all images.</li>
            <li>Ensuring all interactive elements are fully keyboard accessible.</li>
          </ul>
        </section>

      </div>
    </div>
  );
}

export default AccessibilityStatement;
